import { createAsyncThunk } from "@reduxjs/toolkit";
import { postLoginService } from "../../Services";
import { alert } from "../../utils/commonFunctions/alert";

// ==================== MERCHANT COMMISSION APIs ====================

// Get Merchant Commission Dashboard
export const getMerchantCommissionDashboard = createAsyncThunk(
  "commission/getMerchantDashboard",
  async () => {
    try {
      const url = `/api/commissions/merchant/dashboard`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch dashboard" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch dashboard";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get Merchant Commission Transactions
export const getMerchantTransactions = createAsyncThunk(
  "commission/getMerchantTransactions",
  async (status = "") => {
    try {
      const url = status
        ? `/api/commissions/merchant/transactions?status=${status}`
        : `/api/commissions/merchant/transactions`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch transactions" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch transactions";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Approve Commission Transaction (Merchant)
export const approveTransaction = createAsyncThunk(
  "commission/approveTransaction",
  async (transactionId) => {
    try {
      const url = `/api/commissions/merchant/transactions/${transactionId}/approve`;
      const response = await postLoginService.put(url);
      if (response?.success) {
        // alert.success("Transaction approved successfully!");
        return { ...response, transactionId };
      } else {
        alert.error(response?.message || "Failed to approve transaction");
        throw { message: response?.message || "Failed to approve transaction" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to approve transaction";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// ==================== INFLUENCER COMMISSION APIs ====================

// Get Influencer Earnings Dashboard
export const getInfluencerDashboard = createAsyncThunk(
  "commission/getInfluencerDashboard",
  async () => {
    try {
      const url = `/api/commissions/influencer/dashboard`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch dashboard" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch dashboard";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get Influencer Earnings
export const getInfluencerEarnings = createAsyncThunk(
  "commission/getInfluencerEarnings",
  async () => {
    try {
      const url = `/api/commissions/influencer/earnings`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch earnings" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch earnings";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get Influencer Commission Transactions
export const getInfluencerTransactions = createAsyncThunk(
  "commission/getInfluencerTransactions",
  async (status = "") => {
    try {
      const url = status
        ? `/api/commissions/influencer/transactions?status=${status}`
        : `/api/commissions/influencer/transactions`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch transactions" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch transactions";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Request Payout (Influencer)
export const requestPayout = createAsyncThunk(
  "commission/requestPayout",
  async (payoutData) => {
    try {
      const url = `/api/commissions/influencer/payout/request`;
      const response = await postLoginService.post(url, payoutData);
      if (response?.success) {
        // alert.success("Payout request submitted successfully!");
        return response;
      } else {
        alert.error(response?.message || "Failed to request payout");
        throw { message: response?.message || "Failed to request payout" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to request payout";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get Payout History (Influencer) — paginated
export const getPayoutHistory = createAsyncThunk(
  "commission/getPayoutHistory",
  async ({ page = 1, limit = 10 } = {}) => {
    try {
      const url = `/api/commissions/influencer/payout/history?page=${page}&limit=${limit}`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch payout history" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch payout history";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Download Payout Invoice PDF (Influencer and  merchant)
export const downloadPayoutInvoice = createAsyncThunk(
  "commission/downloadPayoutInvoice",
  async ({ batchId, filename }) => {
    // Track whether we already surfaced an error so we never double-toast
    let errorMessage = null;
    try {
      const url = `/api/payouts/invoices/batch/${batchId}/pdf`;
      const blob = await postLoginService.get(url, { responseType: "blob" });

      // If the server returned JSON (e.g. error body) instead of a PDF, parse and surface it
      if (blob?.type === "application/json" || blob?.type?.includes("json")) {
        const text = await blob.text();
        const json = JSON.parse(text);
        errorMessage = json?.error?.message || json?.message || "Failed to download invoice";
        alert.error(errorMessage);
        throw new Error(errorMessage);
      }

      // Trigger browser file download — blob must not go into Redux state
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      return { success: true };
    } catch (error) {
      // Already alerted above — just rethrow without a second toast
      if (errorMessage) throw error;

      // Axios error: response body is a Blob when responseType:"blob" is set
      // Parse it to extract the real API error message
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json?.error?.message || json?.message || "Failed to download invoice";
        } catch (_) {
          errorMessage = "Failed to download invoice";
        }
      } else {
        errorMessage = error?.message || "Failed to download invoice";
      }
      alert.error(errorMessage);
      throw new Error(errorMessage);
    }
  }
);

// Get Sales for a Specific Payout Batch (lazy-loaded, paginated)
export const getPayoutSales = createAsyncThunk(
  "commission/getPayoutSales",
  async ({ payoutId, page = 1, limit = 20 }) => {
    try {
      const url = `/api/commissions/influencer/payout/history/${payoutId}/sales?page=${page}&limit=${limit}`;
      const response = await postLoginService.get(url);
      if (response?.success) {
        return { payoutId, page, ...response };
      } else {
        throw { message: response?.message || "Failed to fetch payout sales" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch payout sales";
      alert.error(errorMessage);
      throw error;
    }
  }
);

// Get Payout History (Merchant) - paginated
export const getMerchantPayoutHistory = createAsyncThunk(
  "commission/getMerchantPayoutHistory",
  async ({ page = 1, limit = 20 } = {}) => {
    try {
      const url = `/api/payouts/merchant/payout/history?page=${page}&limit=${limit}`;
      const response = await postLoginService.get(url);
      if (response?.data) {
        return response;
      } else {
        throw { message: response?.message || "Failed to fetch merchant payout history" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch merchant payout history";
      alert.error(errorMessage);
      throw error;
    }
  }
);
// Get Orders for a Specific Merchant Payout Batch (lazy-loaded, paginated)
export const getMerchantPayoutOrders = createAsyncThunk(
  "commission/getMerchantPayoutOrders",
  async ({ payoutId, page = 1, limit = 20 }) => {
    try {
      const url = `/api/payouts/merchant/payout/history/${payoutId}/orders?page=${page}&limit=${limit}`;
      const response = await postLoginService.get(url);
      if (response?.data) {
        return { payoutId, page, ...response };
      } else {
        throw { message: response?.message || "Failed to fetch payout orders" };
      }
    } catch (error) {
      const errorMessage = error?.message || "Failed to fetch payout orders";
      alert.error(errorMessage);
      throw error;
    }
  }
);